import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    CircularProgress,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    Tabs,
    Tab,
    Grid,
    Card,
    Paper,
    Divider,
    Avatar,
    Dialog,
    Select,
    MenuItem,
    Menu,
    Tooltip,
    Pagination,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Skeleton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from "@mui/icons-material/Add";
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import SendIcon from '@mui/icons-material/Send';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import FilterListIcon from '@mui/icons-material/FilterList';

import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { api } from '../../services/auth';
import { useNotification } from '../../contexts/NotificationContext';
import { getFavorites, addFavorite, removeFavorite } from '../../services/favoriteService';
import { getArchives, addArchive, removeArchive } from '../../services/archiveService';

// Constants from CSS
const COLORS = {
    primary: '#00808a',
    primaryLight: '#e6f2f3',
    sidebarBg: '#ffffff',
    panelBg: '#f8fafc',
    border: '#e2e8f0',
    textMain: '#1e293b',
    textMuted: '#64748b',
    success: '#22c55e',
    warning: '#f59e0b',
    white: '#ffffff'
};

const getPushStatus = (notification) => {
    const now = new Date();
    const publishUp = notification.publishUp ? new Date(notification.publishUp) : null;
    const publishDown = notification.publishDown ? new Date(notification.publishDown) : null;

    if (!notification.isPublished) return { label: 'Brouillon', color: COLORS.warning, dotColor: COLORS.warning };
    if (publishUp && publishUp > now) return { label: 'Programmé', color: COLORS.primary, dotColor: COLORS.primary };
    if (publishDown && publishDown < now) return { label: 'Terminé', color: COLORS.textMuted, dotColor: COLORS.textMuted };
    return { label: 'Publié', color: COLORS.success, dotColor: COLORS.success };
};

const DYNAMIC_FIELDS = [
    { tag: '{contactfield=firstname}', label: 'Prénom' },
    { tag: '{contactfield=lastname}', label: 'Nom' },
    { tag: '{contactfield=email}', label: 'Email' },
    { tag: '{contactfield=phone}', label: 'Téléphone' },
    { tag: '{contactfield=company}', label: 'Entreprise' },
    { tag: '{contactfield=city}', label: 'Ville' },
    { tag: '{contactfield=country}', label: 'Pays' },
    { tag: '{contactfield=position}', label: 'Poste/Fonction' },
];

const DynamicFieldsHelper = ({ onCopy }) => (
    <Box sx={{ p: 1, minWidth: 220 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#fff' }}>
            Variables Dynamiques
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mb: 1.5, opacity: 0.9, color: '#fff' }}>
            Cliquez sur un champ pour copier le tag :
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {DYNAMIC_FIELDS.map((field) => (
                <Box
                    key={field.tag}
                    onClick={() => onCopy(field.tag)}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 0.75,
                        px: 1,
                        borderRadius: 1,
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            transform: 'translateX(4px)'
                        },
                    }}
                >
                    <Box>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#fff' }}>
                            {field.label}
                        </Typography>
                        <code style={{ fontSize: '0.7rem', color: '#cbd5e1', background: 'transparent' }}>{field.tag}</code>
                    </Box>
                    <FileCopyIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                </Box>
            ))}
        </Box>
    </Box>
);


const PushMockup = ({ title, message, icon, image, actionButtonText, timestamp = 'maintenant', url }) => (
    <Box sx={{
        width: '100%',
        bgcolor: COLORS.white,
        borderRadius: 2,
        p: 2,
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
        border: `1px solid ${COLORS.border}`,
        display: 'flex',
        gap: 2
    }}>
        <Box sx={{
            width: 40, height: 40, flexShrink: 0,
            borderRadius: 1, bgcolor: COLORS.primary,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
            {icon ? <Avatar src={icon} variant="rounded" sx={{ width: 40, height: 40 }} /> : null}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: COLORS.textMuted }}>PushNotify</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: COLORS.textMuted }}>{timestamp}</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: COLORS.textMain, mb: 0.25, lineHeight: 1.2 }}>
                {title || 'Titre de la notification'}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#475569', lineHeight: 1.4 }}>
                {message || 'Votre message apparaîtra ici...'}
            </Typography>
            {image && <Box component="img" src={image} sx={{ mt: 1, width: '100%', borderRadius: 1, objectFit: 'cover', maxHeight: 150 }} />}
            {actionButtonText && (
                <Box
                    component={url ? "a" : "div"}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        mt: 1, p: 0.75, bgcolor: '#f1f5f9', border: `1px solid ${COLORS.border}`,
                        borderRadius: 1, textAlign: 'center', fontSize: '0.75rem', fontWeight: 600,
                        color: COLORS.primary, display: 'block', textDecoration: 'none',
                        cursor: url ? 'pointer' : 'default',
                        transition: 'background-color 0.2s',
                        '&:hover': url ? { bgcolor: '#e2e8f0' } : {}
                    }}
                >
                    {actionButtonText}
                </Box>
            )}
        </Box>
    </Box>
);

const PushNotifications = () => {
    const [filtersCollapsed, setFiltersCollapsed] = useState(true);
    const [selectedPushId, setSelectedPushId] = useState(null);
    const [rawPushNotifications, setRawPushNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [metaLoading, setMetaLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('modified');
    const [sortDirection, setSortDirection] = useState('desc');
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [totalPushes, setTotalPushes] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 30, 50];

    const [favorites, setFavorites] = useState(new Set());
    const [archivedPushes, setArchivedPushes] = useState([]);
    const { showNotification } = useNotification();

    const [tabValue, setTabValue] = useState(0);
    const [previewMode, setPreviewMode] = useState('desktop');

    // Modals
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [testModalOpen, setTestModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    // Creation States
    const [creationMode, setCreationMode] = useState(null); // null, 'manual', 'ai'
    const [pushToDelete, setPushToDelete] = useState(null);
    const [pushFormData, setPushFormData] = useState({ name: '', title: '', message: '', url: '', icon: '', image: '', buttonText: '' });
    const [aiPrompt, setAiPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [canGenerate, setCanGenerate] = useState(true);
    const [editTooltipOpen, setEditTooltipOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null);

    // Stats State
    const [stats, setStats] = useState({ clicks: 0, click_rate: 0, sent_count: 0, total_contacts: 0 });
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsAnalysis, setStatsAnalysis] = useState(null);
    const [analysisLoading, setAnalysisLoading] = useState(false);

    const handleCopyTag = (tag) => {
        navigator.clipboard.writeText(tag);
        showNotification(`${tag} copié !`, 'info');
    };

    const handleHeaderMenuOpen = (event) => setHeaderMenuAnchor(event.currentTarget);
    const handleHeaderMenuClose = () => setHeaderMenuAnchor(null);

    // Fetch Logic
    const fetchPushNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/push-notifications', {
                params: {
                    page,
                    page_size: pageSize,
                    search: search || undefined,
                    sort_by: sortBy,
                    sort_direction: sortDirection
                }
            });

            let notifications = response.data.notifications || [];

            // Transform Mautic data to match UI expectations
            notifications = notifications.map(notif => ({
                id: notif.id,
                title: notif.name || notif.heading || 'Sans titre',
                message: notif.message || '',
                url: notif.url || '',
                icon: '', // Mautic doesn't return icon in list
                image: '', // Mautic doesn't return image in list
                actionButtonText: notif.button || '',
                isPublished: notif.isPublished || false,
                publishUp: notif.publishUp || null,
                publishDown: notif.publishDown || null,
                modified: notif.dateModified || notif.dateAdded || new Date().toISOString(),
                created: notif.dateAdded || new Date().toISOString(),
                readCount: notif.readCount || 0,
                sentCount: notif.sentCount || 0
            }));

            setRawPushNotifications(notifications);
            setTotalPushes(response.data.total || 0);
        } catch (err) {
            console.error('Error fetching push notifications:', err);
            showNotification('Erreur lors du chargement des notifications', 'error');
            setRawPushNotifications([]);
            setTotalPushes(0);
        }
        finally { setLoading(false); }
    }, [page, pageSize, search, sortBy, sortDirection]);

    useEffect(() => { fetchPushNotifications(); }, [fetchPushNotifications]);

    // Computed filtered notifications
    const pushNotifications = useMemo(() => {
        let filtered = [...rawPushNotifications];

        if (statusFilter === 'archived') {
            filtered = filtered.filter(n => archivedPushes.includes(n.id));
        } else {
            filtered = filtered.filter(n => !archivedPushes.includes(n.id));

            if (statusFilter === 'favorites') {
                filtered = filtered.filter(n => favorites.has(n.id));
            } else if (statusFilter === 'published') {
                filtered = filtered.filter(n => n.isPublished);
            } else if (statusFilter === 'draft') {
                filtered = filtered.filter(n => !n.isPublished);
            }
        }

        return filtered;
    }, [rawPushNotifications, statusFilter, favorites, archivedPushes]);
    useEffect(() => {
        const loadMeta = async () => {
            try {
                const [favs, archives] = await Promise.all([getFavorites('push'), getArchives('push')]);
                setFavorites(new Set(favs.map(f => Number(f.data_id))));
                setArchivedPushes(archives.map(a => Number(a.data_id)));
            } catch (e) { }
            finally { setMetaLoading(false); }
        };
        loadMeta();
    }, []);

    const selectedPushDetails = useMemo(() => pushNotifications.find(p => p.id === selectedPushId), [selectedPushId, pushNotifications]);

    // Fetch Stats when selected push changes
    useEffect(() => {
        if (selectedPushId) {
            setStatsLoading(true);
            setStatsAnalysis(null); // Reset analysis on selection change
            api.get(`/push-notifications/${selectedPushId}/stats`)
                .then(response => {
                    setStats(response.data);
                })
                .catch(err => {
                    console.error('Error fetching stats:', err);
                    setStats({ clicks: 0, click_rate: 0, sent_count: 0, total_contacts: 0 });
                })
                .finally(() => setStatsLoading(false));
        } else {
            setStats({ clicks: 0, click_rate: 0, sent_count: 0, total_contacts: 0 });
            setStatsAnalysis(null);
        }
    }, [selectedPushId]);

    const handleAnalyzePush = async () => {
        if (!selectedPushId) return;

        setAnalysisLoading(true);
        try {
            const response = await api.get(`/push-notifications/${selectedPushId}/analysis`);
            // response.data contains { stats: {...}, analysis: {...} }
            if (response.data.stats) {
                setStats(response.data.stats);
            }
            setStatsAnalysis(response.data.analysis);
            showNotification('Analyse IA terminée avec succès', 'success');
        } catch (error) {
            console.error('Error analyzing push:', error);
            showNotification("Impossible de générer l'analyse IA", 'error');
        } finally {
            setAnalysisLoading(false);
        }
    };

    // Handlers
    const handleCreatePush = () => {
        setCreateModalOpen(true);
        setCreationMode(null);
        setPushFormData({ name: '', title: '', message: '', url: '', icon: '', image: '', buttonText: '' });
        setAiPrompt('');
        setCanGenerate(true);
    };

    const handleManualCreate = async () => {
        setIsCreating(true);
        try {
            const payload = {
                name: pushFormData.name || 'Notification Sans Nom',
                heading: pushFormData.name, // Use name as heading/title
                message: pushFormData.message,
                url: pushFormData.url,
                button: pushFormData.buttonText,
                isPublished: true,
                language: 'fr'
            };

            const response = await api.post('/push-notifications/new', payload);

            if (response.data && response.data.notification) {
                showNotification('Notification créée avec succès', 'success');
                setCreateModalOpen(false);
                fetchPushNotifications();
                setSelectedPushId(response.data.notification.id);
            }
        } catch (error) {
            console.error('Create error:', error);
            showNotification('Erreur de création', 'error');
        } finally {
            setIsCreating(false);
        }
    };

    const handleAiGenerate = async () => {
        if (!aiPrompt.trim()) {
            showNotification('Veuillez saisir une description', 'warning');
            return;
        }

        setIsGenerating(true);
        try {
            const response = await api.post('/push-notifications/generate', { query: aiPrompt });

            if (response.data.success && response.data.suggestions && response.data.suggestions.length > 0) {
                // Use the first suggestion
                const suggestion = response.data.suggestions[0];
                const config = suggestion.config || {};

                // Map the AI response to the form fields
                setPushFormData({
                    name: config.heading || suggestion.name || 'Notification IA', // Use heading as name, fallback to suggestion name
                    title: config.heading || suggestion.name || '', // Same for title (since we unified them)
                    message: config.message || '',
                    buttonText: config.button || '',
                    url: config.url || '',
                    icon: config.icon || '',
                    image: config.image || '',
                    mobileText: config.mobileText || config.message || '' // Fallback to message if mobileText is empty
                });

                showNotification('Notification générée avec succès!', 'success');
                // Don't close modal - let user review and edit before saving
            } else {
                // Handle case where no suggestions were returned
                const errorMessage = response.data.message || 'Aucune suggestion générée. Essayez de reformuler votre demande.';
                showNotification(errorMessage, 'warning');
            }
        } catch (error) {
            console.error('Error generating push notification:', error);
            const errorMessage = error.response?.data?.detail || 'Erreur lors de la génération de la notification';
            showNotification(errorMessage, 'error');
        } finally {
            setIsGenerating(false);
            setCanGenerate(false);
        }
    };

    const handleDelete = (id) => {
        const push = rawPushNotifications.find(p => p.id === id);
        if (push) {
            setPushToDelete(push);
            setDeleteModalOpen(true);
        }
    };

    const handleConfirmDelete = async () => {
        if (!pushToDelete) return;

        setIsDeleting(true);
        try {
            await api.delete(`/push-notifications/${pushToDelete.id}`);
            showNotification('Supprimé avec succès', 'success');

            // Optimistic update
            setRawPushNotifications(prev => prev.filter(p => p.id !== pushToDelete.id));
            if (selectedPushId === pushToDelete.id) setSelectedPushId(null);

            setDeleteModalOpen(false);
            setPushToDelete(null);
        } catch (e) {
            console.error('Error deleting push:', e);
            showNotification('Erreur lors de la suppression', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleToggleFavorite = async (id, e) => {
        if (e) e.stopPropagation();
        try {
            if (favorites.has(id)) {
                await removeFavorite('push', String(id));
                setFavorites(prev => { const n = new Set(prev); n.delete(id); return n; });
                showNotification('Retiré des favoris', 'success');
            } else {
                await addFavorite('push', String(id));
                setFavorites(prev => new Set([...prev, id]));
                showNotification('Ajouté aux favoris', 'success');
            }
        } catch (e) {
            console.error('Error toggling favorite:', e);
            showNotification('Erreur lors de la modification des favoris', 'error');
        }
    };

    const handleToggleStatus = async (push) => {
        try {
            const updated = { ...push, isPublished: !push.isPublished };

            // Optimistic update on raw data
            setRawPushNotifications(prev => prev.map(p => p.id === push.id ? updated : p));
            showNotification(`Template ${!push.isPublished ? 'activé' : 'désactivé'}`, 'success');

            // Actual API call
            await api.put(`/push-notifications/${push.id}`, { isPublished: !push.isPublished });
        } catch (e) {
            // Revert on error
            console.error('Error toggling status:', e);
            setRawPushNotifications(prev => prev.map(p => p.id === push.id ? push : p));
            showNotification('Erreur lors de la modification du statut', 'error');
        }
    };

    const handleArchive = async (id) => {
        if (!id) return;

        try {
            await addArchive('push', String(id));
            setArchivedPushes(prev => [...prev, id]);
            // No need to manually update list - filtering will handle it
            showNotification('Push archivé', 'success');
        } catch (e) {
            console.error('Error archiving push:', e);
            showNotification('Erreur lors de l\'archivage', 'error');
        }
    };

    const handleUnarchive = async (id) => {
        if (!id) return;
        try {
            await removeArchive('push', String(id));
            setArchivedPushes(prev => prev.filter(pId => pId !== id));
            // No need to manually update list - filtering will handle it
            showNotification('Push restauré', 'success');
        } catch (e) {
            console.error('Error unarchiving:', e);
            showNotification('Erreur lors de la restauration', 'error');
        }
    };

    const handleEditPush = (push) => {
        if (!push) return;

        // Populate the form with the push notification data
        setPushFormData({
            name: push.title || '',
            title: push.title || '',
            message: push.message || '',
            url: push.url || '',
            icon: push.icon || '',
            image: push.image || '',
            buttonText: push.actionButtonText || ''
        });

        setEditModalOpen(true);
    };

    const handleUpdatePush = async () => {
        if (!selectedPushDetails) return;

        setIsUpdating(true);
        try {
            const payload = {
                name: pushFormData.name || 'Notification Sans Nom',
                heading: pushFormData.name, // Use name as heading/title
                message: pushFormData.message,
                url: pushFormData.url,
                button: pushFormData.buttonText
            };

            const response = await api.put(`/push-notifications/${selectedPushDetails.id}`, payload);

            if (response.data && response.data.notification) {
                showNotification('Notification mise à jour avec succès', 'success');
                setEditModalOpen(false);
                fetchPushNotifications();
            }
        } catch (error) {
            console.error('Update error:', error);
            showNotification('Erreur de mise à jour', 'error');
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)', bgcolor: COLORS.white, overflow: 'hidden', mx: 2, mb: 2, borderRadius: 2 }}>

            {/* 1. Filter Sidebar */}
            {!filtersCollapsed && (
                <Box sx={{
                    width: 290,
                    borderRight: `1px solid ${COLORS.border}`, bgcolor: COLORS.white, overflow: 'hidden', pt: 4, px: 2, display: 'flex', flexDirection: 'column'
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, fontSize: '1.25rem', color: COLORS.textMain }}>Push Notifications</Typography>
                    <Button startIcon={<FilterListIcon />} fullWidth sx={{ justifyContent: 'flex-start', borderRadius: 2, py: 1.25, fontWeight: 500, whiteSpace: 'nowrap', color: statusFilter === 'all' ? 'white' : COLORS.textMain, bgcolor: statusFilter === 'all' ? COLORS.primary : 'transparent', mb: 1, '&:hover': { bgcolor: statusFilter === 'all' ? COLORS.primary : COLORS.panelBg }, textTransform: 'none' }} onClick={() => setStatusFilter('all')}>Tous les push notifications</Button>
                    <Button startIcon={<StarIcon />} fullWidth sx={{ justifyContent: 'flex-start', borderRadius: 2, py: 1.25, fontWeight: 500, color: statusFilter === 'favorites' ? 'white' : COLORS.textMain, bgcolor: statusFilter === 'favorites' ? COLORS.primary : 'transparent', mb: 1, '&:hover': { bgcolor: statusFilter === 'favorites' ? COLORS.primary : COLORS.panelBg }, textTransform: 'none' }} onClick={() => setStatusFilter('favorites')}>Favoris</Button>
                    <Button startIcon={<ArchiveIcon />} fullWidth sx={{ justifyContent: 'flex-start', borderRadius: 2, py: 1.25, fontWeight: 500, color: statusFilter === 'archived' ? 'white' : COLORS.textMain, bgcolor: statusFilter === 'archived' ? COLORS.primary : 'transparent', '&:hover': { bgcolor: statusFilter === 'archived' ? COLORS.primary : COLORS.panelBg }, textTransform: 'none' }} onClick={() => setStatusFilter('archived')}>Archivés</Button>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, mb: 1, display: 'block' }}>STATUTS</Typography>
                    <Button
                        fullWidth
                        onClick={() => setStatusFilter('published')}
                        sx={{
                            justifyContent: 'flex-start',
                            fontWeight: 400,
                            color: statusFilter === 'published' ? 'white' : COLORS.textMain,
                            bgcolor: statusFilter === 'published' ? COLORS.primary : 'transparent',
                            gap: 1.5,
                            '&:hover': { bgcolor: statusFilter === 'published' ? COLORS.primary : COLORS.panelBg },
                            textTransform: 'none'
                        }}
                    >
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: COLORS.success, border: statusFilter === 'published' ? '1px solid white' : 'none' }} />
                        Publié
                    </Button>
                    <Button
                        fullWidth
                        onClick={() => setStatusFilter('draft')}
                        sx={{
                            justifyContent: 'flex-start',
                            fontWeight: 400,
                            color: statusFilter === 'draft' ? 'white' : COLORS.textMain,
                            bgcolor: statusFilter === 'draft' ? COLORS.primary : 'transparent',
                            gap: 1.5,
                            '&:hover': { bgcolor: statusFilter === 'draft' ? COLORS.primary : COLORS.panelBg },
                            textTransform: 'none'
                        }}
                    >
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: COLORS.warning, border: statusFilter === 'draft' ? '1px solid white' : 'none' }} />
                        Brouillon
                    </Button>
                </Box>
            )}

            {/* 2. List Panel */}
            <Box sx={{ width: '40%', borderRight: `1px solid ${COLORS.border}`, display: 'flex', flexDirection: 'column', bgcolor: COLORS.white, height: '100%', overflow: 'hidden' }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Tooltip title={filtersCollapsed ? "Afficher les filtres" : "Masquer les filtres"}>
                            <IconButton
                                onClick={() => setFiltersCollapsed(!filtersCollapsed)}
                                size="small"
                                sx={{
                                    bgcolor: COLORS.white,
                                    border: `1px solid ${COLORS.border}`,
                                    width: 32,
                                    height: 32,
                                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                    '&:hover': { bgcolor: COLORS.primaryLight }
                                }}
                            >
                                {filtersCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>Templates Push</Typography>
                    </Box>
                    <IconButton onClick={handleCreatePush} sx={{ color: COLORS.primary }} size="small"><AddIcon /></IconButton>
                </Box>
                <Box sx={{ px: 2, pb: 2 }}>
                    <TextField fullWidth size="small" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: COLORS.textMuted }} /></InputAdornment>, sx: { bgcolor: COLORS.panelBg, '& fieldset': { border: 'none' }, borderRadius: 2 } }} />
                </Box>
                <Box sx={{ px: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Trier par:</Typography>
                        <Select variant="standard" disableUnderline value={sortBy} onChange={e => setSortBy(e.target.value)} sx={{ fontSize: '0.8rem', fontWeight: 500 }}><MenuItem value="modified">Date de modification</MenuItem><MenuItem value="created">Date de création</MenuItem><MenuItem value="name">Nom</MenuItem></Select>
                    </Box>
                    <IconButton size="small" onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')}>{sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}</IconButton>
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {loading || metaLoading ? (
                        <Box sx={{ p: 4, textAlign: 'center', color: COLORS.textMuted }}>
                            <CircularProgress size={24} sx={{ color: COLORS.primary, mb: 2 }} />
                            <Typography>Chargement des notifications...</Typography>
                        </Box>
                    ) : pushNotifications.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center', color: COLORS.textMuted }}>Aucun résultat</Box>
                    ) : (
                        <List disablePadding>
                            {pushNotifications.map(push => {
                                const status = getPushStatus(push);
                                return (
                                    <ListItem
                                        key={push.id}
                                        button
                                        onClick={() => setSelectedPushId(push.id)}
                                        sx={{
                                            borderBottom: 'none',
                                            borderLeft: 'none',
                                            bgcolor: selectedPushId === push.id ? COLORS.primaryLight : 'transparent',
                                            py: 1.5,
                                            position: 'relative',
                                            '&:hover .item-actions': { opacity: 1, pointerEvents: 'auto' }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', width: '100%', position: 'relative' }}>
                                            {/* Favorite Icon (Left) */}
                                            <IconButton size="small" onClick={(e) => handleToggleFavorite(push.id, e)} sx={{ p: 0.5, color: COLORS.textMuted }}>
                                                {favorites.has(push.id) ? <StarIcon sx={{ fontSize: '1.1rem', color: COLORS.warning }} /> : <StarBorderIcon sx={{ fontSize: '1.1rem' }} />}
                                            </IconButton>

                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ color: COLORS.textMain, fontSize: '0.9rem' }}>{push.title || 'Sans titre'}</Typography>
                                            </Box>

                                            {/* Inline Actions (Visible on Hover) */}
                                            <Box className="item-actions" sx={{ opacity: 0, pointerEvents: 'none', transition: 'opacity 0.2s', display: 'flex', gap: 0.5, position: 'absolute', right: 24, bgcolor: selectedPushId === push.id ? COLORS.primaryLight : COLORS.white, px: 1, borderRadius: 1 }}>
                                                <Tooltip title={push.isPublished ? "Désactiver" : "Activer"}>
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleStatus(push); }}>
                                                        {push.isPublished ? <ToggleOnIcon fontSize="small" sx={{ color: COLORS.primary }} /> : <ToggleOffIcon fontSize="small" sx={{ color: COLORS.textMuted }} />}
                                                    </IconButton>
                                                </Tooltip>
                                                {statusFilter === 'archived' ? (
                                                    <Tooltip title="Restaurer">
                                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUnarchive(push.id); }}>
                                                            <UnarchiveIcon fontSize="small" sx={{ color: COLORS.textMuted }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Archiver">
                                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleArchive(push.id); }}>
                                                            <ArchiveIcon fontSize="small" sx={{ color: COLORS.textMuted }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Supprimer">
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(push.id); }}>
                                                        <DeleteOutlineIcon fontSize="small" sx={{ color: COLORS.textMuted }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>

                                            {/* Status Dot */}
                                            <Box className="status-dot" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: status.dotColor, flexShrink: 0 }} />
                                        </Box>
                                    </ListItem>
                                )
                            })}
                        </List>
                    )}
                </Box>
                <Box sx={{ py: 1.5, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderTop: `1px solid ${COLORS.border}`, bgcolor: COLORS.white }}>
                    <Select
                        size="small"
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setPage(1);
                        }}
                        sx={{ mr: 2, width: 72 }}
                    >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                            <MenuItem key={n} value={n}>{n}</MenuItem>
                        ))}
                    </Select>
                    <Pagination
                        count={Math.ceil(totalPushes / pageSize)}
                        page={page}
                        onChange={(e, newPage) => setPage(newPage)}
                        color="primary"
                        size="small"
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: COLORS.textMuted,
                                '&:hover': {
                                    bgcolor: COLORS.panelBg,
                                },
                                '&.Mui-selected': {
                                    bgcolor: COLORS.primary,
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: COLORS.primary,
                                    },
                                },
                            },
                        }}
                    />
                </Box>
            </Box>

            {/* 3. Editor Panel */}
            <Box sx={{ flex: 1, bgcolor: COLORS.panelBg, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                {selectedPushDetails ? (
                    <>
                        {/* Header */}
                        <Box sx={{ p: 3, bgcolor: COLORS.white, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedPushDetails.title}</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Modifier">
                                    <IconButton size="small" sx={{ color: COLORS.textMuted }} onClick={() => handleEditPush(selectedPushDetails)}>
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                                <IconButton size="small" sx={{ color: COLORS.textMuted }} onClick={handleHeaderMenuOpen}>
                                    <MoreVertIcon />
                                </IconButton>
                                <Menu
                                    anchorEl={headerMenuAnchor}
                                    open={Boolean(headerMenuAnchor)}
                                    onClose={handleHeaderMenuClose}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    PaperProps={{
                                        sx: {
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: 2,
                                            minWidth: 150
                                        }
                                    }}
                                >
                                    <MenuItem onClick={() => { handleToggleStatus(selectedPushDetails); handleHeaderMenuClose(); }} sx={{ gap: 1.5, py: 1.2 }}>
                                        {selectedPushDetails.isPublished ? <ToggleOffIcon fontSize="small" sx={{ color: COLORS.textMuted }} /> : <ToggleOnIcon fontSize="small" sx={{ color: COLORS.primary }} />}
                                        <Typography variant="body2">{selectedPushDetails.isPublished ? 'Désactiver' : 'Activer'}</Typography>
                                    </MenuItem>
                                    <MenuItem onClick={() => { handleArchive(selectedPushDetails.id); handleHeaderMenuClose(); }} sx={{ gap: 1.5, py: 1.2 }}>
                                        <ArchiveIcon fontSize="small" sx={{ color: COLORS.textMuted }} />
                                        <Typography variant="body2">Archiver</Typography>
                                    </MenuItem>
                                    <MenuItem onClick={() => { handleDelete(selectedPushDetails.id); handleHeaderMenuClose(); }} sx={{ gap: 1.5, py: 1.2, color: '#ef4444' }}>
                                        <DeleteOutlineIcon fontSize="small" />
                                        <Typography variant="body2">Supprimer</Typography>
                                    </MenuItem>
                                </Menu>
                            </Box>
                        </Box>

                        {/* Content Container */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3, bgcolor: COLORS.panelBg, overflowY: 'auto' }}>

                            {/* Preview Section */}
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ color: COLORS.textMuted, mb: 1, fontWeight: 700, fontSize: '0.75rem' }}>Aperçu de la Notification</Typography>
                                <Box sx={{ maxWidth: '100%', bgcolor: COLORS.white, borderRadius: 2, border: `1px solid ${COLORS.border}`, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', p: 0 }}>
                                    <PushMockup {...selectedPushDetails} icon={null} image={null} />
                                </Box>
                            </Box>

                            {/* Tabs */}
                            <Box sx={{ borderBottom: `1px solid ${COLORS.border}`, mb: 2 }}>
                                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', mr: 2, px: 0 } }} TabIndicatorProps={{ sx: { bgcolor: COLORS.primary, height: 2 } }}>
                                    <Tab label="Analyse & IA" value={0} />
                                </Tabs>
                            </Box>

                            {/* Tab Content: Analyse & IA */}
                            {tabValue === 0 && (
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: COLORS.textMain, fontSize: '1rem' }}>Statistiques du Push</Typography>
                                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                                        {[
                                            { label: 'DESTINATAIRES', value: statsLoading ? <Skeleton variant="text" width={60} /> : (stats?.total_contacts?.toLocaleString() || '0') },
                                            { label: 'ENVOYÉS', value: selectedPushDetails?.sentCount?.toLocaleString() || '0' },
                                            { label: 'Clics Uniques', value: statsLoading ? <Skeleton variant="text" width={60} /> : (stats?.clicks?.toLocaleString() || '0') },
                                            { label: 'Taux de Clics', value: statsLoading ? <Skeleton variant="text" width={60} /> : (stats?.click_rate ? `${stats.click_rate}%` : '0%') }
                                        ].map((stat, i) => (
                                            <Grid item xs={12} md={3} key={i}>
                                                <Box sx={{ bgcolor: COLORS.white, p: 2, borderRadius: 2, boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}>
                                                    <Typography variant="caption" sx={{ color: COLORS.textMuted, fontWeight: 700, display: 'block', mb: 0.5, letterSpacing: 0.5, fontSize: '0.7rem' }}>{stat.label}</Typography>
                                                    <Typography variant="h4" sx={{ fontWeight: 700, color: COLORS.textMain, fontSize: '1.5rem' }}>{stat.value}</Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    {/* AI Analysis Box */}
                                    <Box sx={{ bgcolor: '#effcf6', border: `1px solid ${COLORS.primary}40`, borderRadius: 2, p: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: COLORS.primary }}>
                                            <SmartToyIcon fontSize="small" />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Analyse IA Avancée</Typography>
                                        </Box>

                                        {analysisLoading ? (
                                            <Box sx={{ py: 2 }}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}><Skeleton variant="text" width="100%" /></Grid>
                                                    <Grid item xs={12}><Skeleton variant="text" width="80%" /></Grid>
                                                    <Grid item xs={12}><Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} /></Grid>
                                                </Grid>
                                            </Box>
                                        ) : statsAnalysis ? (
                                            <>
                                                <Typography variant="body2" sx={{ color: COLORS.textMain, mb: 1.5, lineHeight: 1.5, fontSize: '0.875rem' }}>
                                                    {statsAnalysis.analysis || "Analyse indisponible."}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: COLORS.textMain, mb: 1.5, lineHeight: 1.5, fontSize: '0.875rem' }}>
                                                    Basé sur les performances actuelles, ce template a un taux d'engagement de <Box component="span" sx={{ fontWeight: 700, color: COLORS.primary }}>{statsAnalysis.engagement_rate || "N/A"}</Box>, ce qui est <Box component="span" sx={{ fontStyle: 'italic' }}>{statsAnalysis.benchmark_comparison || "comparable à la moyenne"}</Box>.
                                                </Typography>
                                                <Box sx={{ bgcolor: COLORS.white, borderLeft: `4px solid ${COLORS.primary}`, p: 1.5, borderRadius: 1 }}>
                                                    <Typography variant="body2" sx={{ color: COLORS.textMain, fontSize: '0.875rem' }}>
                                                        <Box component="span" sx={{ fontWeight: 700 }}>Suggestion IA :</Box> {statsAnalysis.suggestion || "Aucune suggestion pour le moment."}
                                                    </Typography>
                                                </Box>
                                            </>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                                                <Typography variant="body2" sx={{ color: COLORS.textMuted, mb: 2 }}>
                                                    Aucune analyse disponible pour cette notification.
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    color="warning"
                                                    startIcon={<SmartToyIcon />}
                                                    onClick={handleAnalyzePush}
                                                    sx={{
                                                        bgcolor: '#fff7ed',
                                                        color: '#c2410c',
                                                        border: '1px solid #fed7aa',
                                                        boxShadow: 'none',
                                                        fontWeight: 600,
                                                        textTransform: 'none',
                                                        '&:hover': {
                                                            bgcolor: '#ffedd5',
                                                            boxShadow: 'none'
                                                        }
                                                    }}
                                                >
                                                    Lancer l'analyse IA
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </>
                ) : (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: COLORS.textMuted }}>
                        <NotificationsActiveIcon sx={{ fontSize: 64, mb: 2, opacity: 0.2 }} />
                        <Typography variant="h6">Sélectionnez une notification</Typography>
                    </Box>
                )}
            </Box>

            {/* --- Unified Create Modal --- */}
            <Dialog
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                maxWidth={!creationMode ? "md" : "lg"}
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                        height: !creationMode ? 'auto' : '650px',
                        maxWidth: !creationMode ? '650px' : '1000px'
                    }
                }}
            >
                {!creationMode ? (
                    /* Selection Screen */
                    <Box sx={{ p: 5 }}>
                        <Box sx={{ textAlign: 'center', mb: 5 }}>
                            <Typography variant="h4" sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 1, color: COLORS.textMain }}>Comment souhaitez-vous créer ?</Typography>
                            <Typography sx={{ color: COLORS.textMuted }}>Choisissez une méthode pour commencer votre notification.</Typography>
                        </Box>

                        <Grid container spacing={3}>
                            <Grid item xs={6}>
                                <Box
                                    onClick={() => setCreationMode('ai')}
                                    sx={{
                                        border: `1px solid ${COLORS.border}`, borderRadius: 4, p: 4, cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)', borderColor: COLORS.primary, boxShadow: '0 12px 20px -8px rgba(0,128,138,0.15)' }
                                    }}
                                >
                                    <Box sx={{ width: 48, height: 48, mx: 'auto', mb: 2, color: COLORS.primary }}><SmartToyIcon sx={{ fontSize: 40 }} /></Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Créer avec l'IA</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box
                                    onClick={() => setCreationMode('manual')}
                                    sx={{
                                        border: `1px solid ${COLORS.border}`, borderRadius: 4, p: 4, cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)', borderColor: COLORS.primary, boxShadow: '0 12px 20px -8px rgba(0,128,138,0.15)' }
                                    }}
                                >
                                    <Box sx={{ width: 48, height: 48, mx: 'auto', mb: 2, color: COLORS.textMain }}><EditIcon sx={{ fontSize: 40 }} /></Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Créer manuellement</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                        <Box sx={{ mt: 4, textAlign: 'center' }}>
                            <Button onClick={() => setCreateModalOpen(false)} sx={{ color: COLORS.textMuted }}>Annuler</Button>
                        </Box>
                    </Box>
                ) : (
                    /* Editor Layout (Split View) */
                    <Box sx={{ display: 'flex', height: '100%' }}>
                        {/* Left: Form/Prompt */}
                        <Box sx={{ flex: 1, p: 4, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ mb: 3 }}>
                                {creationMode === 'ai' && (
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, bgcolor: COLORS.primaryLight, color: COLORS.primary, borderRadius: 10, fontSize: '0.75rem', fontWeight: 700, mb: 2, textTransform: 'uppercase' }}>
                                        <SmartToyIcon sx={{ fontSize: 14 }} /> IA Assistée
                                    </Box>
                                )}
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>{creationMode === 'ai' ? "Générer avec l'IA" : "Créer une notification"}</Typography>
                                <Typography variant="body2" sx={{ color: COLORS.textMuted, mt: 0.5 }}>
                                    {creationMode === 'ai' ? "Décrivez la promotion ou l'alerte que vous souhaitez envoyer." : "Remplissez les détails de votre notification."}
                                </Typography>
                            </Box>

                            <Box sx={{ flex: 1 }}>
                                {creationMode === 'ai' ? (
                                    <Box sx={{ bgcolor: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 2, p: 2, mb: 2 }}>
                                        <TextField
                                            fullWidth multiline rows={4} variant="standard"
                                            placeholder="ex: Créer une notification pour une promo de -50% sur les chaussures de sport ce weekend..."
                                            InputProps={{ disableUnderline: true }}
                                            value={aiPrompt}
                                            onChange={e => {
                                                setAiPrompt(e.target.value);
                                                setCanGenerate(true);
                                            }}
                                        />
                                        <Divider sx={{ my: 2 }} />
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            disabled={isGenerating || !canGenerate}
                                            onClick={handleAiGenerate}
                                            startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : <SmartToyIcon />}
                                            sx={{ bgcolor: COLORS.primary, py: 1.5, fontWeight: 600, textTransform: 'none' }}
                                        >
                                            {isGenerating ? 'Génération en cours...' : 'Générer le brouillon'}
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>Nom / Titre <span style={{ color: 'red' }}>*</span></Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="ex: Promo Janvier"
                                                value={pushFormData.name || ''}
                                                onChange={e => setPushFormData({ ...pushFormData, name: e.target.value, title: e.target.value })}
                                                required
                                                error={!pushFormData.name}
                                            />
                                            <Typography variant="caption" sx={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Ce nom sera utilisé comme titre de la notification.</Typography>
                                        </Box>
                                        {/* Titre field hidden - populated by Nom */}
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', display: 'block' }}>Message <span style={{ color: 'red' }}>*</span></Typography>
                                                <Tooltip
                                                    title={<DynamicFieldsHelper onCopy={handleCopyTag} />}
                                                    placement="right"
                                                    arrow
                                                    interactive
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help', color: COLORS.textMuted, '&:hover': { color: COLORS.primary }, ml: 1 }}>
                                                        <HelpOutlineIcon sx={{ fontSize: 16 }} />
                                                        <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Variables dynamiques</Typography>
                                                    </Box>
                                                </Tooltip>
                                            </Box>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                size="small"
                                                value={pushFormData.message}
                                                onChange={e => setPushFormData({ ...pushFormData, message: e.target.value })}
                                                placeholder="Contenu..."
                                                required
                                                error={!pushFormData.message}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>URL {pushFormData.buttonText && <span style={{ color: 'red' }}>*</span>}</Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={pushFormData.url}
                                                onChange={e => setPushFormData({ ...pushFormData, url: e.target.value })}
                                                placeholder="https://..."
                                                required={!!pushFormData.buttonText}
                                                error={!!pushFormData.buttonText && !pushFormData.url}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>Texte Bouton d'Action {pushFormData.url && <span style={{ color: 'red' }}>*</span>}</Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={pushFormData.buttonText}
                                                onChange={e => setPushFormData({ ...pushFormData, buttonText: e.target.value })}
                                                placeholder="ex: En savoir plus"
                                                required={!!pushFormData.url}
                                                error={!!pushFormData.url && !pushFormData.buttonText}
                                            />
                                        </Box>
                                    </Box>

                                )}
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, pt: 3, borderTop: `1px solid ${COLORS.border}` }}>
                                <Button onClick={() => setCreationMode(null)} sx={{ color: COLORS.textMuted }}>Retour</Button>
                                <Button
                                    onClick={handleManualCreate}
                                    disabled={(creationMode === 'ai' && !pushFormData.title) || isCreating || (creationMode === 'manual' && (!pushFormData.name || !pushFormData.message)) || (!!pushFormData.url !== !!pushFormData.buttonText)}
                                    variant="contained"
                                    sx={{ bgcolor: COLORS.primary }}
                                >
                                    {isCreating ? <CircularProgress size={24} color="inherit" /> : (creationMode === 'ai' ? 'Utiliser ce brouillon' : 'Enregistrer')}
                                </Button>
                            </Box>
                        </Box>

                        {/* Right: Preview Background */}
                        <Box sx={{ width: '45%', bgcolor: COLORS.panelBg, borderLeft: `1px solid ${COLORS.border}`, p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative' }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ color: COLORS.textMuted, fontWeight: 700, textTransform: 'uppercase', mb: 3, textAlign: 'center' }}
                            >
                                {creationMode === 'ai' ? 'Aperçu IA' : 'Aperçu en direct'}
                            </Typography>
                            {creationMode === 'ai' && (
                                <Box
                                    onMouseEnter={() => setEditTooltipOpen(true)}
                                    onMouseLeave={() => setEditTooltipOpen(false)}
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        right: 12,
                                    }}
                                >
                                    <IconButton
                                        size="medium"
                                        onClick={() => setCreationMode('manual')}
                                        disabled={!pushFormData.title.trim() && !pushFormData.message.trim()}
                                        sx={{
                                            color: COLORS.primary,
                                            bgcolor: COLORS.white,
                                            boxShadow: '0 2px 4px rgba(15, 23, 42, 0.18)',
                                            '&:hover': {
                                                bgcolor: COLORS.primaryLight
                                            }
                                        }}
                                    >
                                        <EditIcon sx={{ fontSize: 22 }} />
                                    </IconButton>
                                    {editTooltipOpen && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '100%',
                                                right: 0,
                                                mt: 0.5,
                                                bgcolor: 'rgba(15, 23, 42, 0.92)',
                                                color: '#fff',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 1,
                                                fontSize: '0.72rem',
                                                whiteSpace: 'nowrap',
                                                boxShadow: '0 4px 10px rgba(15, 23, 42, 0.4)',
                                                zIndex: 10,
                                            }}
                                        >
                                            Passer en édition manuelle
                                        </Box>
                                    )}
                                </Box>
                            )}

                            <Box sx={{ transform: 'scale(1.1)' }}>
                                <PushMockup
                                    title={pushFormData.title || (creationMode === 'ai' ? (isGenerating ? 'Génération...' : 'En attente...') : 'Titre du push')}
                                    message={pushFormData.message || (creationMode === 'ai' ? (isGenerating ? 'Veuillez patienter...' : 'Décrivez votre besoin à gauche.') : 'Votre notification ici...')}
                                    icon={pushFormData.icon}
                                    image={pushFormData.image}
                                    actionButtonText={pushFormData.buttonText}
                                    url={pushFormData.url}
                                />
                            </Box>

                            <Typography variant="caption" sx={{ textAlign: 'center', color: COLORS.textMuted, mt: 4, display: 'block' }}>
                                L'apparence peut varier selon l'appareil.
                            </Typography>
                        </Box>
                    </Box>
                )
                }
            </Dialog >

            {/* --- Edit Modal --- */}
            <Dialog
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                        height: '650px',
                        maxWidth: '1000px'
                    }
                }}
            >
                <Box sx={{ display: 'flex', height: '100%' }}>
                    {/* Left: Form */}
                    <Box sx={{ flex: 1, p: 4, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>Modifier la notification</Typography>
                            <Typography variant="body2" sx={{ color: COLORS.textMuted, mt: 0.5 }}>
                                Modifiez les détails de votre notification.
                            </Typography>
                        </Box>

                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>Nom / Titre <span style={{ color: 'red' }}>*</span></Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="ex: Promo Janvier"
                                    value={pushFormData.name || ''}
                                    onChange={e => setPushFormData({ ...pushFormData, name: e.target.value, title: e.target.value })}
                                    required
                                    error={!pushFormData.name}
                                />
                                <Typography variant="caption" sx={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Ce nom sera utilisé comme titre de la notification.</Typography>
                            </Box>
                            {/* Titre field hidden */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', display: 'block' }}>Message <span style={{ color: 'red' }}>*</span></Typography>
                                    <Tooltip
                                        title={<DynamicFieldsHelper onCopy={handleCopyTag} />}
                                        placement="right"
                                        arrow
                                        interactive
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help', color: COLORS.textMuted, '&:hover': { color: COLORS.primary }, ml: 1 }}>
                                            <HelpOutlineIcon sx={{ fontSize: 16 }} />
                                            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Variables dynamiques</Typography>
                                        </Box>
                                    </Tooltip>
                                </Box>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    size="small"
                                    value={pushFormData.message}
                                    onChange={e => setPushFormData({ ...pushFormData, message: e.target.value })}
                                    placeholder="Contenu..."
                                    required
                                    error={!pushFormData.message}
                                />
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>URL {pushFormData.buttonText && <span style={{ color: 'red' }}>*</span>}</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={pushFormData.url}
                                    onChange={e => setPushFormData({ ...pushFormData, url: e.target.value })}
                                    placeholder="https://..."
                                    required={!!pushFormData.buttonText}
                                    error={!!pushFormData.buttonText && !pushFormData.url}
                                />
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>Texte Bouton d'Action {pushFormData.url && <span style={{ color: 'red' }}>*</span>}</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={pushFormData.buttonText}
                                    onChange={e => setPushFormData({ ...pushFormData, buttonText: e.target.value })}
                                    placeholder="ex: En savoir plus"
                                    required={!!pushFormData.url}
                                    error={!!pushFormData.url && !pushFormData.buttonText}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, pt: 3, borderTop: `1px solid ${COLORS.border}` }}>
                            <Button onClick={() => setEditModalOpen(false)} sx={{ color: COLORS.textMuted }}>Annuler</Button>
                            <Button onClick={handleUpdatePush} disabled={isUpdating || !pushFormData.name || !pushFormData.message || (!!pushFormData.url !== !!pushFormData.buttonText)} variant="contained" sx={{ bgcolor: COLORS.primary }}>
                                {isUpdating ? <CircularProgress size={24} color="inherit" /> : 'Enregistrer'}
                            </Button>
                        </Box>
                    </Box>

                    {/* Right: Preview */}
                    <Box sx={{ width: '45%', bgcolor: COLORS.panelBg, borderLeft: `1px solid ${COLORS.border}`, p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Typography variant="subtitle2" sx={{ color: COLORS.textMuted, fontWeight: 700, textTransform: 'uppercase', mb: 3, textAlign: 'center' }}>
                            Aperçu en direct
                        </Typography>

                        <Box sx={{ transform: 'scale(1.1)' }}>
                            <PushMockup
                                title={pushFormData.title || 'Titre du push'}
                                message={pushFormData.message || 'Votre notification ici...'}
                                icon={pushFormData.icon}
                                image={pushFormData.image}
                                actionButtonText={pushFormData.buttonText}
                                url={pushFormData.url}
                            />
                        </Box>

                        <Typography variant="caption" sx={{ textAlign: 'center', color: COLORS.textMuted, mt: 4, display: 'block' }}>
                            L'apparence peut varier selon l'appareil.
                        </Typography>
                    </Box>
                </Box>
            </Dialog>

            {/* --- Delete Confirmation Modal --- */}
            <Dialog
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        minWidth: '400px'
                    }
                }}
            >
                <DialogTitle id="delete-dialog-title" sx={{ fontWeight: 700 }}>
                    {"Confirmer la suppression"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Êtes-vous sûr de vouloir supprimer la notification "{pushToDelete?.title || 'Sans titre'}" ? Cette action est irréversible.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 0 }}>
                    <Button onClick={() => setDeleteModalOpen(false)} disabled={isDeleting} sx={{ color: COLORS.textMuted }}>Annuler</Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error" autoFocus disabled={isDeleting}>
                        {isDeleting ? <CircularProgress size={24} color="inherit" /> : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box >
    );
};

export default PushNotifications;
